import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useOrganizationStore } from '../stores/StoreProvider';
import { Search, X } from 'lucide-react';

const OrganizationAutocomplete = observer(({ 
  value, 
  onSelect, 
  placeholder = "Search organizations...",
  className = ""
}) => {
  const organizationStore = useOrganizationStore();
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Set initial value if provided
    if (value && typeof value === 'object') {
      setSelectedOrg(value);
      setSearchText(value.name);
    }
  }, [value]);

  const handleSearchChange = (text) => {
    setSearchText(text);
    setSelectedOrg(null);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      organizationStore.searchOrganizations(text);
    }, 300);
  };

  const handleSelectOrganization = (org) => {
    setSelectedOrg(org);
    setSearchText(org.name);
    setShowDropdown(false);
    organizationStore.clearSearchResults();
    onSelect(org);
  };

  const handleInputFocus = () => {
    if (searchText.length >= 2) {
      setShowDropdown(organizationStore.searchResults.length > 0);
    }
  };

  const handleClearSelection = () => {
    setSelectedOrg(null);
    setSearchText('');
    onSelect(null);
  };

  // Update dropdown visibility when search results change
  useEffect(() => {
    if (searchText.length >= 2) {
      setShowDropdown(organizationStore.searchResults.length > 0);
    } else {
      setShowDropdown(false);
    }
  }, [organizationStore.searchResults, searchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {selectedOrg && (
          <button
            onClick={handleClearSelection}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
        {organizationStore.searchLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {organizationStore.searchResults.map((item) => (
            <button
              key={item.id.toString()}
              onClick={() => handleSelectOrganization(item)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-8 h-8 rounded object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.name}
                  </p>
                  {item.team_name && (
                    <p className="text-xs text-blue-600 truncate">
                      {item.team_name}
                    </p>
                  )}
                  {item.website && (
                    <p className="text-xs text-gray-500 truncate">
                      {item.website}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default OrganizationAutocomplete; 